import React, { useRef, useState } from 'react';
import { motion, useMotionValueEvent, useReducedMotion, useScroll } from 'framer-motion';
import type { Product } from '../../types/product';
import { easeEnter } from '../../utils/motion';
import { CatchVisual } from './CatchVisual';
import { MatchVisual } from './MatchVisual';
import { PairingVisual } from './PairingVisual';
type ProductScrollStoryProps = {
  product: Product;
};
function Centrepiece({
  product,
  active
}: {
  product: Product;
  active: number;
}) {
  if (product.centrepiece === 'catch') return <CatchVisual active={active} data-id="element-754" data-mp="8r6ytf5fgnl" />;
  if (product.centrepiece === 'pairing') return <PairingVisual active={active} data-id="element-755" data-mp="f4ypzqetmc1a" />;
  return <MatchVisual active={active} data-id="element-756" data-mp="f3xc7e6f7bfv" />;
}

/**
 * The one large set-piece on each product page. The visual is pinned while the
 * campaign flow advances around it, driven by scroll position rather than a
 * timer, so nothing has to be waited out.
 *
 * Every step's text is in the DOM at all times — scrolling changes emphasis, it
 * is never the only way to reach content.
 */
export function ProductScrollStory({
  product
}: ProductScrollStoryProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const count = product.steps.length;
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });
  useMotionValueEvent(scrollYProgress, 'change', value => {
    const next = Math.floor(value * count);
    setActive(Math.max(0, Math.min(count - 1, next)));
  });
  if (reduceMotion) {
    return <section className="bg-paper px-5 py-24 md:px-10 md:py-28 lg:pl-20" aria-labelledby="story-heading" data-id="element-757" data-mp="m3ezxi5bay91">
        <div className="mx-auto max-w-shell" data-id="element-758" data-mp="r16xossn1el5">
          <StoryHeading product={product} data-id="element-759" data-mp="qzvwn0qkukxq" />
          <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_0.8fr] lg:items-start" data-id="element-760" data-mp="2nvlbz9a5hxnr">
            <ol className="flex flex-col" data-id="element-761" data-mp="2st3dielu9alq">
              {product.steps.map((step, index) => <li key={step.title} className="border-t border-ink/15 py-6 first:border-t-0 first:pt-0" data-id="element-762" data-mp="3w40qci7avkjl" data-mpi={index}>
                  <StepBody product={product} step={step} index={index} active data-id="element-763" data-mp="3r7ij9bohza43" />
                </li>)}
            </ol>
            <Centrepiece product={product} active={count - 1} data-id="element-764" data-mp="40881i0duc99" />
          </div>
        </div>
      </section>;
  }
  return <section ref={ref} className="relative bg-paper" style={{
    height: `${count * 90}vh`
  }} aria-labelledby="story-heading" data-id="element-765" data-mp="iwphu4sxbitp">
      <div className="sticky top-[4.25rem] flex min-h-[calc(100vh-4.25rem)] items-center px-5 py-16 md:px-10 lg:pl-20" data-id="element-766" data-mp="rqu2g6hgry0s">
        <div className="mx-auto w-full max-w-shell" data-id="element-767" data-mp="nps0vj02r9vn">
          <StoryHeading product={product} data-id="element-768" data-mp="nr37rrotgxqz" />
          <div className="mt-12 grid gap-12 lg:grid-cols-[1fr_0.8fr] lg:items-center" data-id="element-769" data-mp="11pjn270qvwkq">
            <ol className="flex flex-col" data-id="element-770" data-mp="wq6par8c553d">
              {product.steps.map((step, index) => <li key={step.title} className="border-t border-ink/15 py-5 first:border-t-0 first:pt-0" data-id="element-771" data-mp="1gaxgg2e2wpwu" data-mpi={index}>
                  <StepBody product={product} step={step} index={index} active={index === active} data-id="element-772" data-mp="1l7xo2hqku0q2" />
                </li>)}
            </ol>
            <Centrepiece product={product} active={active} data-id="element-773" data-mp="xjo65qwhhx47" />
          </div>
        </div>
      </div>
    </section>;
}
function StoryHeading({
  product
}: {
  product: Product;
}) {
  return <>
      <p className="text-xs font-extrabold tracking-[0.16em] text-ink/55" data-id="element-774" data-mp="nnkunufqsm4l">
        HOW {product.name.toUpperCase()} WORKS
      </p>
      <h2 id="story-heading" className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[0.9] md:text-6xl" data-id="element-775" data-mp="slw2jzmh8mk6">
        One product. One connected flow.
      </h2>
    </>;
}
function StepBody({
  product,
  step,
  index,
  active
}: {
  product: Product;
  step: Product['steps'][number];
  index: number;
  active: boolean;
}) {
  return <motion.div animate={{
    opacity: active ? 1 : 0.4
  }} transition={{
    duration: 0.24,
    ease: easeEnter
  }} className="flex gap-5" data-id="element-776" data-mp="6m6y0uloi1xx">
      <span className={`font-display text-sm font-bold ${active ? 'text-signal' : 'text-ink/45'}`} data-id="element-777" data-mp="3t1i5nvgs32ri">
        0{index + 1}
      </span>
      <div data-id="element-778" data-mp="3ox2240un07fg">
        <p className={`text-lg font-extrabold tracking-[0.06em] ${active ? product.theme.onPaper : 'text-ink'}`} data-id="element-779" data-mp="3k01vnotvl2nt">
          {step.title}
        </p>
        <p className="mt-2 max-w-md leading-relaxed text-ink/70" data-id="element-780" data-mp="3f17essfgxo6c">{step.copy}</p>
      </div>
    </motion.div>;
}